import * as gtag from './gtag';
import logging, { saveUserAfterFormSubmit } from './logging';
import { checkIsHuman, verifyRecaptchaToken } from './recaptcha';
import { checkValidEmail } from './zeroBounce';

export const HUBSPOT_TRACKING_TOKEN = 'hubspotutk';

interface ContextType {
  pageUri: string;
  pageName: string;
}

export const submitHubSpotForm = async (
  formId: string,
  portalId: string,
  form: { [key: string]: string | undefined },
  context: ContextType,
  newsletter?: boolean,
) => {
  const fields = [];
  for (const attr in form) {
    fields.push({ name: attr, value: form[attr] });
  }
  const communicationIds: number[] | undefined = process.env
    .NEXT_PUBLIC_HUBSPOT_COMMUNICATION_SUBSCRIPTION_TYPE_IDS
    ? JSON.parse(process.env.NEXT_PUBLIC_HUBSPOT_COMMUNICATION_SUBSCRIPTION_TYPE_IDS)
    : undefined;

  const hubspotTrackingToken = document.cookie
    ?.split(`${HUBSPOT_TRACKING_TOKEN}=`)[1]
    ?.split(';')[0];

  return fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
    method: 'post',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      fields,
      context: { ...context, ...(hubspotTrackingToken && { hutk: hubspotTrackingToken }) },
      ...(!!communicationIds &&
        communicationIds.length > 0 && {
          legalConsentOptions: {
            consent: {
              consentToProcess: true,
              text: 'I agree to allow App Masters to store and process my personal data.',
              ...(newsletter && {
                communications: communicationIds.map((value) => ({
                  value: true,
                  subscriptionTypeId: Number(value),
                  text: 'I agree to receive marketing communications from App Masters.',
                })),
              }),
            },
          },
        }),
    }),
  });
};

type SubmitContactParams = {
  hubspotFormId: string;
  hubspotPortalId: string;
  hubspotFields: { [key: string]: string | undefined };
  hubspotContext: { pageName: string; pageUri: string };
  newsletter?: boolean;
  contactVariables: {
    name: string;
    email: string;
    message?: string;
    phone?: string;
    formTitle?: string;
    url?: string;
    wizardId?: string;
    bufferPdf?: string;
  };
  createContact: (options: { variables: Record<string, unknown> }) => Promise<unknown>;
  executeRecaptcha?: (action: string) => Promise<string>;
  gtagConversionEvent?: string;
  setError?: (error: string) => void;
  setLoading?: (loading: boolean) => void;
};

export const submitContactAndHubSpot = async (params: SubmitContactParams) => {
  const {
    hubspotFormId,
    hubspotPortalId,
    hubspotFields,
    hubspotContext,
    newsletter,
    contactVariables,
    createContact,
    executeRecaptcha,
    gtagConversionEvent,
    setError,
    setLoading,
  } = params;

  logging.info('3 - submitContactAndHubSpot started', {
    hasHubspotFormId: !!hubspotFormId,
    hasHubspotPortalId: !!hubspotPortalId,
    hasRecaptcha: !!executeRecaptcha,
    hasGtagConversionEvent: !!gtagConversionEvent,
    contactEmail: contactVariables.email,
    contactName: contactVariables.name,
    formTitle: contactVariables.formTitle,
    url: contactVariables.url,
    hasWizardId: !!contactVariables.wizardId,
    hasBufferPdf: !!contactVariables.bufferPdf,
  });

  saveUserAfterFormSubmit({ name: contactVariables.name, email: contactVariables.email });

  const { response, validEmail } = await checkValidEmail(contactVariables.email);
  if (!validEmail) {
    logging.info('4 - Invalid email on Contact Form', { email: contactVariables.email, response });
    setError?.('email');
    setLoading?.(false);
    return;
  }

  if (executeRecaptcha) {
    const token = await executeRecaptcha('contact_conversion');
    const verify = await verifyRecaptchaToken(token);

    if (!verify.success) {
      logging.error('5 - [contact_conversion] Recaptcha failed', contactVariables);
      setError?.('captcha');
      setLoading?.(false);
      return;
    } else if (!checkIsHuman(verify)) {
      logging.error('6 - [contact_conversion] User with low recaptcha score', contactVariables);
      setError?.('captcha');
      setLoading?.(false);
      return;
    }
  }

  try {
    if (gtagConversionEvent)
      gtag.event({ action: 'conversion', conversionEventId: gtagConversionEvent });

    if (hubspotFormId && hubspotPortalId) {
      logging.info('7 - submitContactAndHubSpot sending to HubSpot', {
        hubspotFormId,
        hubspotPortalId,
        hubspotFieldKeys: Object.keys(hubspotFields),
        pageUri: hubspotContext.pageUri,
        pageName: hubspotContext.pageName,
      });

      const hsResponse = await submitHubSpotForm(
        hubspotFormId,
        hubspotPortalId,
        hubspotFields,
        hubspotContext,
        newsletter,
      );
      const hsResult = await hsResponse.json();
      if (hsResult.status === 'error') {
        logging.error('8 - Error on Hubspot submit', {
          error: hsResult.errors,
          errorMessage: hsResult.message,
          contactVariables,
          form: { hubspotFormId, hubspotPortalId },
          referrer: document.referrer,
        });
        setError?.('hubspot');
        setLoading?.(false);
        return;
      }

      logging.info('9 - submitContactAndHubSpot HubSpot submission succeeded', {
        hubspotFormId,
        contactEmail: contactVariables.email,
      });
    } else {
      logging.info('10 - submitContactAndHubSpot skipped HubSpot submission', {
        reason: 'missing hubspotFormId or hubspotPortalId',
        hasHubspotFormId: !!hubspotFormId,
        hasHubspotPortalId: !!hubspotPortalId,
      });
    }

    logging.info('11 - submitContactAndHubSpot calling createContact', {
      mutation: 'createContactAndSendEmailWebsite',
      contactEmail: contactVariables.email,
      formTitle: contactVariables.formTitle,
      hasMessage: !!contactVariables.message,
      hasPhone: !!contactVariables.phone,
      hasWizardId: !!contactVariables.wizardId,
      hasBufferPdf: !!contactVariables.bufferPdf,
    });

    await createContact({
      variables: contactVariables,
    });

    logging.info('12 - submitContactAndHubSpot createContact succeeded', {
      mutation: 'createContactAndSendEmailWebsite',
      contactEmail: contactVariables.email,
      formTitle: contactVariables.formTitle,
    });
    logging.info('Form has been submitted successfully', contactVariables);
    setLoading?.(false);
  } catch (error) {
    logging.error('Error on submit', {
      error,
      errorMessage: (error as { message: string }).message,
      contactVariables,
    });
    setError?.('submit');
    setLoading?.(false);
    throw error;
  }
};
